import { useEffect } from 'react';
import { observer } from 'mobx-react-lite';
import styled from 'styled-components';
import { ElevatedButton, Collaborators } from '@producer/ui-kit';
import { PersonIcon, PlusIcon } from '@radix-ui/react-icons';

import { ProjectOverviewHeader } from '../../../features/projects/components/project-overview-header';
import { OverviewTab } from '../components/overview-tab';
import { useCurrentProject } from '../../../hooks/use-current-project';
import { Header } from '../project.page';
import { TaskPanel } from '../../../features/tasks/components/task.panel';
import { uiStore } from '../../../core/stores/ui-store';
import { openModal } from '../../../core/modal/open-modal';
import { ProjectCollaboratorModal } from '../../../features/projects/modals/project-collaborator.modal';
import { RenameDialog } from '../../../components/dialogs/rename.dialog';
import { Stage } from '../../../app/entities/stage';
import { catchError } from '../../../core/catch-error';
import { context } from '../../../app/context';
import { PrioritySelector } from '../../../features/process/components/priority-selector';

export const ProjectRootScreen = observer(() => {
  const project = useCurrentProject()!;
  const currentMember = context.member;

  useEffect(() => {
    uiStore.currentLocation = 'project.overview';
  }, []);

  const openCreateStageModal = () => {
    openModal(RenameDialog, {
      defaultName: '',
      title: 'Create stage',
      label: 'Stage name',
      placeholder: 'Enter stage name',
      onSubmit: async (name: string) => {
        try {
          await Stage.create(project._id, name);
        } catch (e) {
          catchError(e);
        }
      },
    });
  };

  const openCollaboratorModal = () => {
    openModal(ProjectCollaboratorModal, {
      project,
    });
  };

  return (
    <>
      <Header>
        Overview
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.8rem' }}>
          <PrioritySelector
            priority={project.priority}
            onChange={(priority) => project.update({ priority })}
            withTitle
          />

          <Collaborators onClick={openCollaboratorModal} collaborators={project.collaborators} />

          {['owner', 'admin', 'creator'].includes(currentMember?.role || '') && (
            <ElevatedButton text="Invite" icon={<PersonIcon />} onClick={openCollaboratorModal} />
          )}
        </div>
      </Header>

      <Content>
        <ProjectOverviewHeader project={project} />

        <Container>
          <ActionsRow>
            <ElevatedButton
              text="Create stage"
              icon={<PlusIcon />}
              onClick={openCreateStageModal}
            />
          </ActionsRow>

          <OverviewTab project={project} onCreateStage={openCreateStageModal} />

          {uiStore.isTaskPanelOpen && !!uiStore.taskPanelTargetId && (
            <TaskPanel targetId={uiStore.taskPanelTargetId} />
          )}
        </Container>
      </Content>
    </>
  );
});

const Container = styled.div`
  margin: 1.6rem 1.6rem 0;
  display: flex;
  flex-direction: column;
  flex: 1;
  max-width: 100%;
`;

const ActionsRow = styled.div`
  display: flex;
  justify-content: flex-end;
`;

const Content = styled.div`
  height: 100%;
  overflow-x: hidden;
  overflow-y: auto;
  position: relative;
  display: flex;
  flex-direction: column;
`;
